import { Injectable } from "@angular/core";
import { MessageService } from '@progress/kendo-angular-l10n';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AfMessageService extends MessageService {
    constructor(private translateService: TranslateService) {
      super();

      // Update whenever the language is changed and
      // when the TranslateService is first initialized.
      this.translateService.onLangChange.subscribe({
        next: () => {
          this.translateServiceInitialized = true;
          this.notify();
        }
      });
    }

  // Note on the English translation file:
  // The English translation file does not contain any
  // keys so that the default English ones are used.
  // You can add them to override the original text if
  // desired.

  // TranslateService needs a while to initialize and load
  // the translation, so we return default messages until
  // the first language has been loaded.
  private translateServiceInitialized = false;

  // If true, uses the log and Ui to warn of keys missing
  // from the map below. Kendo might look for keys that
  // are not (yet) visible, so the warning log can get
  // quite busy. The best way for finding actually missing
  // keys is to enable this and then look for the messages
  // directly in the UI.
  private readonly showMissingKendoKeys: boolean = false;

  private readonly messages: Record<string, string> =
  {
    'kendo.dropdownlist.noDataText': 'KENDO.DROPDOWNLIST.NO-DATA-TEXT',
    'kendo.grid.filterAfterOperator': 'KENDO.GRID.FILTER.AFTER-OPERATOR',
    'kendo.grid.filterAfterOrEqualOperator': 'KENDO.GRID.FILTER.AFTER-OR-EQUAL-OPERATOR',
    'kendo.grid.filterAndLogic': 'KENDO.GRID.FILTER.AND-LOGIC',
    'kendo.grid.filterBeforeOperator': 'KENDO.GRID.FILTER.BEFORE-OPERATOR',
    'kendo.grid.filterBeforeOrEqualOperator': 'KENDO.GRID.FILTER.BEFORE-OR-EQUAL-OPERATOR',
    'kendo.grid.filterBooleanAll': 'KENDO.GRID.FILTER.BOOLEAN-ALL',
    'kendo.grid.filterClearButton' : 'KENDO.GRID.FILTER.CLEAR-BUTTON',
    'kendo.grid.filterContainsOperator': 'KENDO.GRID.FILTER.CONTAINS-OPERATOR',
    'kendo.grid.filterEndsWithOperator': 'KENDO.GRID.FILTER.ENDS-WITH-OPERATOR',
    'kendo.grid.filterEqOperator' : 'KENDO.GRID.FILTER.EQ-OPERATOR',
    'kendo.grid.filterFilterButton': 'KENDO.GRID.FILTER.FILTER-BUTTON',
    'kendo.grid.filterGtOperator': 'KENDO.GRID.FILTER.GT-OPERATOR',
    'kendo.grid.filterGteOperator': 'KENDO.GRID.FILTER.GTE-OPERATOR',
    'kendo.grid.filterIsEmptyOperator' : 'KENDO.GRID.FILTER.IS-EMPTY-OPERATOR',
    'kendo.grid.filterIsFalse': 'KENDO.GRID.FILTER.IS-FALSE',
    'kendo.grid.filterIsNotEmptyOperator' : 'KENDO.GRID.FILTER.IS-NOT-EMPTY-OPERATOR',
    'kendo.grid.filterIsNotNullOperator' : 'KENDO.GRID.FILTER.IS-NOT-NULL-OPERATOR',
    'kendo.grid.filterIsNullOperator' : 'KENDO.GRID.FILTER.IS-NULL-OPERATOR',
    'kendo.grid.filterIsTrue': 'KENDO.GRID.FILTER.IS-TRUE',
    'kendo.grid.filterLtOperator': 'KENDO.GRID.FILTER.LT-OPERATOR',
    'kendo.grid.filterLteOperator': 'KENDO.GRID.FILTER.LTE-OPERATOR',
    'kendo.grid.filterNotContainsOperator': 'KENDO.GRID.FILTER.NOT-CONTAINS-OPERATOR',
    'kendo.grid.filterNotEqOperator' : 'KENDO.GRID.FILTER.NOT-EQ-OPERATOR',
    'kendo.grid.filterOrLogic': 'KENDO.GRID.FILTER.OR-LOGIC',
    'kendo.grid.filterStartsWithOperator': 'KENDO.GRID.FILTER.STARTS-WITH-OPERATOR',
    'kendo.upload.dropFilesHere' : 'KENDO.UPLOAD.DROP-HERE',
    'kendo.upload.fileStatusUploaded' : 'KENDO.UPLOAD.FILE-STATUS-UPLOADED',
    'kendo.upload.headerStatusUploaded' : 'KENDO.UPLOAD.HEADER-STATUS-UPLOADED',
    'kendo.upload.select' : 'KENDO.UPLOAD.SELECT-FILES'
  };

  public override get(key: string): string | undefined {
    if (!this.translateServiceInitialized) {
      // TranslateService not yet initialized, return default Kendo text for now.
      return undefined;
    }

    const translationKey = this.messages[key];
    if (!translationKey) {
      if (this.showMissingKendoKeys) {
        console.warn(`No Kendo key found for: ${key}`);
        return `No Kendo key: ${key}`;
      }
      // Show default Kendo text. These messages can
      // be displayed under rare circumstances or hidden
      // deeply in some controls, so replacing them with
      // errors could be worse than an occasional English
      // text.
      return undefined;
    }

    const translation = this.translate(translationKey);
    if (translation == translationKey) { // Translation not found in <lang>.json
      const currentLang = this.translateService.currentLang();
      if (currentLang == 'en') {
        // Show default Kendo text, English does not
        // need an explicit translation.
        return undefined;
      }
      console.warn(`No '${currentLang}' translation found for: ${translationKey}`);
    }

    // Can be the translation key itself if no translation
    // is found. This is the same behavior as for all other
    // missing translations.
    return translation;
  }

  private translate(translationKey: string) : string {
    return this.translateService.instant(translationKey);
  }
}